import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Person as MemberIcon,
  Schedule as PendingIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { apiUrl } from '../../config/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TeamSelector: React.FC = () => {
  const { user, teams, currentTeam, invitations, selectTeam, refreshTeams, acceptInvitation, declineInvitation } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [processingInvitation, setProcessingInvitation] = useState<number | null>(null);

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      
      await axios.post(apiUrl('/api/teams'), {
        name: teamName.trim(),
        description: teamDescription.trim()
      });

      await refreshTeams();
      setCreateDialogOpen(false);
      setTeamName('');
      setTeamDescription('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create team');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: number) => {
    try {
      setProcessingInvitation(invitationId);
      await acceptInvitation(invitationId);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDeclineInvitation = async (invitationId: number) => {
    try {
      setProcessingInvitation(invitationId);
      await declineInvitation(invitationId);
    } catch (err: any) {
      setError(err.message || 'Failed to decline invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? <AdminIcon /> : <MemberIcon />;
  };

  const getRoleColor = (role: string): "default" | "primary" | "secondary" => {
    return role === 'admin' ? 'primary' : 'default';
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome, {user?.displayName}!
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Select a team to get started or create a new one.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={`My Teams (${teams.length})`} />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Invitations
                  {invitations.length > 0 && (
                    <Chip size="small" label={invitations.length} color="warning" />
                  )}
                </Box>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Current Team */}
          {currentTeam && (
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon color="primary" />
                    Current Team: {currentTeam.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentTeam.description || 'No description'}
                  </Typography>
                </Box>
                <Chip
                  icon={getRoleIcon(currentTeam.role)}
                  label={currentTeam.role}
                  color={getRoleColor(currentTeam.role)}
                  variant="filled"
                />
              </Box>
            </Paper>
          )}

          {/* Teams List */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              {teams.length === 0 ? 'No Teams' : 'All Teams'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Team
            </Button>
          </Box>

          {teams.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No teams yet
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create your first team to start collaborating with colleagues.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Your First Team
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
              {teams.map((team) => (
                <Card
                  key={team.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 },
                    border: currentTeam?.id === team.id ? '2px solid' : '1px solid',
                    borderColor: currentTeam?.id === team.id ? 'primary.main' : 'divider'
                  }}
                  onClick={() => selectTeam(team)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {team.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {team.description || 'No description'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {team.member_count} {team.member_count === 1 ? 'member' : 'members'}
                      </Typography>
                      <Chip
                        icon={getRoleIcon(team.role)}
                        label={team.role}
                        size="small"
                        color={getRoleColor(team.role)}
                      />
                    </Box>
                  </CardContent>
                  {currentTeam?.id === team.id && (
                    <CardActions>
                      <Chip
                        label="Current Team"
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ ml: 1 }}
                      />
                    </CardActions>
                  )}
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Invitations */}
          {invitations.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <PendingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No pending invitations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You'll see team invitations here when someone invites you to join their team.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {invitations.map((invitation) => (
                <Card key={invitation.id}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {invitation.team_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {invitation.team_description || 'No description'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Invited by: {invitation.invited_by_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(invitation.created_at).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      disabled={processingInvitation === invitation.id}
                    >
                      {processingInvitation === invitation.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        'Accept'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      disabled={processingInvitation === invitation.id}
                    >
                      Decline
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>

        {/* Create Team Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Team Name"
              fullWidth
              variant="outlined"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description (Optional)"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={teamDescription}
              onChange={(e) => setTeamDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateTeam}
              variant="contained"
              disabled={isCreating || !teamName.trim()}
            >
              {isCreating ? <CircularProgress size={20} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TeamSelector;